import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export function useAccessLogs(filters = {}) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    totalAttempts: 0,
    successfulAttempts: 0,
    failedAttempts: 0,
    anomalies: 0
  });

  useEffect(() => {
    fetchAccessLogs();

    // Setup realtime subscription with error handling
    let subscription = null;
    
    try {
      subscription = supabase
        .channel('access_logs_changes')
        .on('postgres_changes',
          { event: 'INSERT', schema: 'public', table: 'access_logs' },
          (payload) => {
            console.log('New access log:', payload);
            fetchAccessLogs(); // Refetch when new log added
          }
        )
        .subscribe((status, err) => {
          if (status === 'SUBSCRIBED') {
            console.log('✅ Realtime subscription active');
          }
          if (status === 'CHANNEL_ERROR') {
            console.error('❌ Realtime subscription error:', err);
          }
        });
    } catch (err) {
      console.error('Failed to setup realtime:', err);
      // Continue without realtime if it fails
    }

    return () => {
      if (subscription) {
        try {
          subscription.unsubscribe();
        } catch (err) {
          console.error('Error unsubscribing:', err);
        }
      }
    };
  }, [JSON.stringify(filters)]); // Use JSON.stringify to properly track filter changes

  const fetchAccessLogs = async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('Fetching access logs with filters:', filters);

      // First, fetch access logs only
      let query = supabase
        .from('access_logs')
        .select('*')
        .order('attempted_at', { ascending: false });

      // Apply filters
      if (filters.dateFrom) {
        query = query.gte('attempted_at', filters.dateFrom);
      }
      if (filters.dateTo) {
        query = query.lte('attempted_at', filters.dateTo);
      }
      if (filters.result) {
        query = query.eq('result', filters.result);
      }

      const { data: accessLogsData, error: logsError } = await query;

      if (logsError) {
        console.error('Error fetching access logs:', logsError);
        throw logsError;
      }

      console.log('Access logs fetched:', accessLogsData?.length || 0);

      // Then fetch devices and users separately (to avoid JOIN issues)
      const deviceIds = [...new Set(accessLogsData.map(log => log.device_id).filter(Boolean))];
      const userIds = [...new Set(accessLogsData.map(log => log.user_id).filter(Boolean))];

      // Fetch devices
      let devicesMap = {};
      if (deviceIds.length > 0) {
        const { data: devicesData, error: devicesError } = await supabase
          .from('devices')
          .select('id, device_id, name, location')
          .in('id', deviceIds);

        if (!devicesError && devicesData) {
          devicesMap = Object.fromEntries(
            devicesData.map(d => [d.id, d])
          );
        }
      }

      // Fetch user profiles
      let usersMap = {};
      if (userIds.length > 0) {
        const { data: usersData, error: usersError } = await supabase
          .from('user_profiles')
          .select('id, email, full_name, role')
          .in('id', userIds);

        if (!usersError && usersData) {
          usersMap = Object.fromEntries(
            usersData.map(u => [u.id, u])
          );
        }
      }

      // Transform data to match existing structure
      const transformedLogs = accessLogsData.map((log, index) => {
        const device = devicesMap[log.device_id] || {};
        const user = usersMap[log.user_id] || {};

        return {
          id: log.id || index,
          timestamp: new Date(log.attempted_at),
          userId: user.email || 'Unknown',
          userName: user.full_name || 'Unknown User',
          userRole: user.role || 'Unknown',
          doorId: device.device_id || 'Unknown',
          doorName: device.name || 'Unknown Door',
          location: device.location || 'Unknown Location',
          action: log.result === 'granted' ? 'Granted' : 'Denied',
          result: log.result,
          isAnomaly: false, // Will be enhanced with anomaly detection
          ipAddress: log.ip_address || 'N/A',
          device: log.user_agent || 'Unknown Device',
          method: log.access_method || 'Unknown',
          duration: Math.floor(Math.random() * 2000) + 500, // Mock duration
          notes: log.additional_data?.notes || ''
        };
      });

      console.log('Transformed logs:', transformedLogs.length);

      setLogs(transformedLogs);

      // Calculate stats
      const totalAttempts = transformedLogs.length;
      const successfulAttempts = transformedLogs.filter(l => l.result === 'granted').length;
      const failedAttempts = transformedLogs.filter(l => l.result === 'denied' || l.result === 'failed').length;
      const anomalies = transformedLogs.filter(l => l.isAnomaly).length;

      setStats({
        totalAttempts,
        successfulAttempts,
        failedAttempts,
        anomalies
      });

      setError(null);
      console.log('✅ Access logs loaded successfully');

    } catch (err) {
      console.error('❌ Error fetching access logs:', err);
      setError(err.message || 'Failed to fetch access logs');
      
      // Set empty data on error
      setLogs([]);
      setStats({
        totalAttempts: 0,
        successfulAttempts: 0,
        failedAttempts: 0,
        anomalies: 0
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    logs,
    loading,
    error,
    stats,
    refetch: fetchAccessLogs
  };
}